import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Calendar, Home, ArrowRight, Phone } from "lucide-react";

const BookingConfirmationCard = () => {
  useEffect(() => {
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const navigate = useNavigate();

  return (
    <div className="w-full max-w-3xl mx-auto space-y-6">
      {/* Success Animation Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Success Icon & Message */}
        <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 px-4 sm:px-6 py-6 text-center relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-200/20 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-40 h-40 bg-emerald-200/20 rounded-full translate-y-20 -translate-x-20"></div>

          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-100 to-emerald-100 mb-4 md:mb-2 shadow-lg shadow-green-200/50">
              <CheckCircle2
                className="text-green-600 size-16 md:size-12"
                strokeWidth={2}
              />
            </div>

            <h2 className="text-2xl md:text-xl font-bold text-gray-900 mb-2">
              Booking Confirmed!
            </h2>

            <p className="text-sm md:text-xs text-gray-600 max-w-md mx-auto">
              Your service has been successfully booked. We'll get in touch with
              you shortly.
            </p>
          </div>
        </div>

        {/* What's Next Section */}
        <div className="px-4 sm:px-6 py-6 space-y-4">
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            What's Next?
          </h3>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-blue-50 to-blue-50/50 rounded-xl border border-blue-100/50">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                <span className="text-sm font-bold text-white">1</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  Confirmation Call
                </p>
                <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                  Our team will call you to confirm the booking details
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-purple-50 to-purple-50/50 rounded-xl border border-purple-100/50">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                <span className="text-sm font-bold text-white">2</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  Professional Assignment
                </p>
                <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                  A verified professional will be assigned to your service
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gradient-to-br from-green-50 to-green-50/50 rounded-xl border border-green-100/50">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                <span className="text-sm font-bold text-white">3</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  Service Delivery
                </p>
                <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">
                  Professional arrives at your scheduled time and location
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="px-4 sm:px-6 pb-6 space-y-3">
          <div className="pt-4 border-t border-gray-100"></div>

          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Quick Actions
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <button
              onClick={() => navigate("/my-bookings")}
              className="
                flex items-center justify-center gap-2
                px-4 py-3 rounded-xl
                bg-gray-50 border border-gray-200
                text-gray-700 font-semibold text-sm
                hover:bg-gray-100 hover:border-gray-300
                transition-all
              "
            >
              <Calendar size={18} />
              My Bookings
            </button>

            <button
              onClick={() => navigate("/")}
              className="
                flex items-center justify-center gap-2
                px-4 py-3 rounded-xl
                bg-gray-50 border border-gray-200
                text-gray-700 font-semibold text-sm
                hover:bg-gray-100 hover:border-gray-300
                transition-all
              "
            >
              <Home size={18} />
              Go Home
            </button>
          </div>
        </div>
      </div>

      {/* Book More Services CTA */}
      <div className="bg-gradient-to-br from-orange-50 via-orange-50/50 to-transparent rounded-2xl p-5 sm:p-6 text-center border border-orange-100/50">
        <p className="text-sm text-gray-600 mb-4">Need more services?</p>

        <button
          onClick={() => {
            navigate("/services");
            window.location.reload();
          }}
          className="
            inline-flex items-center justify-center gap-2
            px-6 py-3 rounded-xl
            bg-gradient-to-r
            from-[#f0653a] to-[#e4572f]
            hover:from-[#e4572f] hover:to-[#d94d28]
            text-white font-semibold
            shadow-md shadow-[#f0653a]/30
            hover:shadow-lg hover:shadow-[#f0653a]/40
            transition-all duration-200 ease-out
            active:scale-[0.97]
          "
        >
          Book More Services
          <ArrowRight size={18} />
        </button>
      </div>

      {/* Support Info */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 text-center">
        <p className="text-xs text-gray-500 mb-2">Need assistance?</p>
        <a
          href="tel:+919998887776"
          className="inline-flex items-center gap-2 text-sm font-semibold text-primaryColor hover:text-primaryColor/80 transition"
        >
          <Phone size={14} />
          +91 999 888 7776
        </a>
      </div>
    </div>
  );
};

export default BookingConfirmationCard;
